import { broadcastDayKey } from './broadcast-day.js'

/**
 * Chevauchements encore réels entre propositions d'un dépôt de modèle.
 *
 * Jumeau de `backend/src/lib/proposal-overlaps.ts` : mêmes bornes, même
 * adjacence, même journée d'antenne. `preview()` fige ses avertissements dans
 * la génération ; `apply()` les recalcule sur ce qui sera réellement posé, en
 * retirant les créneaux ignorés. Sans ce jumeau, le dialogue continuait de
 * compter comme bloquant le créneau du dessus alors que le serveur, lui,
 * l'aurait accepté : ignorer le créneau débordé ne débloquait rien à l'écran.
 *
 * Le débordement est imputé au créneau qui déborde — celui du dessus — jamais à
 * celui qu'il écrase. Les durées substituées restent l'affaire du serveur :
 * l'appelant écarte ces créneaux avant de lire le résultat.
 */
type ProposalGeometry = {
  proposalId: string
  startTime: string
  endTime: string
  preRoll: { durationSec: number } | null
  postRoll: { durationSec: number } | null
  /** Jingle de liaison devant le pre-roll : il ouvre l'unité. */
  junctionJingle?: { durationSec: number } | null
}

export function unresolvedOverlapIds(
  proposals: readonly ProposalGeometry[],
  isSkipped: (proposalId: string) => boolean,
): Set<string> {
  // Habillage et pub compris : sans eux, chaque post-roll passerait pour un
  // chevauchement. Même unité que `proposalUnitBounds()` côté serveur.
  const units = proposals
    .filter(proposal => !isSkipped(proposal.proposalId))
    .map(proposal => {
      const start = new Date(proposal.startTime).getTime()
      return {
        proposalId:  proposal.proposalId,
        day:         broadcastDayKey(new Date(start)),
        unitStartMs: start - ((proposal.preRoll?.durationSec ?? 0) + (proposal.junctionJingle?.durationSec ?? 0)) * 1000,
        unitEndMs:   new Date(proposal.endTime).getTime() + (proposal.postRoll?.durationSec ?? 0) * 1000,
      }
    })

  const overlapping = new Set<string>()
  for (let index = 0; index < units.length - 1; index++) {
    const current = units[index]
    const next = units[index + 1]
    if (current.day !== next.day) continue
    if (next.unitStartMs < current.unitEndMs) overlapping.add(current.proposalId)
  }
  return overlapping
}
